import { API, unwrapData } from '../axios-instance'
import { BatchResult } from '../shared/batch.types'
import { CursorResult, PageResult } from '../shared/types'
import {
  BatchRemoveMembersSearchParams,
  CreateTeamParams,
  ListTeamMembersParams,
  ListTeamsEmbeds,
  ListTeamsParams,
  SearchTeamsParams,
  Team,
  TeamAutomation,
  TeamListSegmentType,
  TeamMember,
  TeamSegment,
  TeamSegmentParam,
  TeamSearchResult,
  TeamSegmentTypePreview,
  TeamSegmentTypePreviewParams,
  TeamSegmentsPreview,
  TeamsSegmentsSyncStatus,
  UpdateTeamParams,
} from './types'

export const listTeams = async (params?: ListTeamsParams) => {
  return await API.get<PageResult<Team>>('/identity/teams', { params }).then(unwrapData)
}

/**
 * Cursor-paginated team search, for filter dropdowns that page a long list in as
 * the user scrolls. Pass the `next` of the previous page back as `cursor`.
 */
export const searchTeams = async (params?: SearchTeamsParams) => {
  return await API.get<CursorResult<TeamSearchResult>>('/identity/teams/search', { params }).then(
    unwrapData,
  )
}

export const listTeamTypes = async () => {
  return await API.get<TeamListSegmentType[]>('/identity/teams/types').then(unwrapData)
}

export const getTeam = async (teamId: string, embeds?: ListTeamsEmbeds[]) => {
  return await API.get<Team>(`/identity/teams/${teamId}`, { params: { embeds } }).then(unwrapData)
}

export const deleteTeam = async (teamId: string) => {
  return await API.delete<boolean>(`/identity/teams/${teamId}`)
}

export const createTeam = async ({ name, description, teamType, hidden }: CreateTeamParams) => {
  return await API.post<Team>('/identity/teams', {
    name,
    description,
    teamType,
    hidden,
  }).then(unwrapData)
}

export const listTeamMembers = async (teamId: string, params?: ListTeamMembersParams) => {
  return await API.get<PageResult<TeamMember>>(`/identity/teams/${teamId}/teamMembers`, {
    params,
  }).then(unwrapData)
}

export const batchRemoveTeamMembers = async (
  teamId: string,
  params: BatchRemoveMembersSearchParams,
): Promise<BatchResult> => {
  return await API.delete<BatchResult>(`/identity/teams/${teamId}/teamMembers`, {
    data: params,
  }).then(unwrapData)
}

export const updateTeam = async (
  teamId: string,
  { name, description, hidden }: UpdateTeamParams,
) => {
  return await API.patch<Team>(`/identity/teams/${teamId}`, {
    name,
    description,
    hidden,
  }).then(unwrapData)
}

export const getTeamAutomation = async (teamId: string) => {
  return await API.get<TeamAutomation>(`/identity/teamAutomations/teams/${teamId}`).then(unwrapData)
}

export const listSegments = async () => {
  return await API.get<TeamSegment[]>('/identity/teams/segments').then(unwrapData)
}

export const updateSegments = async (updateSegmentParams: TeamSegmentParam[]) => {
  return await API.put<TeamSegment[]>('/identity/teams/segments', updateSegmentParams).then(
    unwrapData,
  )
}

export const previewSegments = async () => {
  return await API.get<TeamSegmentsPreview[]>('/identity/teams/segments/preview').then(unwrapData)
}

export const previewSegmentByType = async (params: TeamSegmentTypePreviewParams) => {
  return await API.get<PageResult<TeamSegmentTypePreview>>(
    `/identity/teams/segments/preview/${params.teamSegmentType}`,
    {
      params,
    },
  ).then(unwrapData)
}

export const getSegmentsSyncStatus = async () => {
  return await API.get<TeamsSegmentsSyncStatus>('/identity/teams/segments/status').then(unwrapData)
}
